
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Position} from './Position';
import {FieldModelPosition} from './Position';

/**
 * AccountPositions struct
 */
class AccountPositions {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!Map=} positions
   * @constructor
   */
  constructor (login = new UInt64(0, 0), positions = new Map()) {
    this.Login = login
    this.Positions = positions
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountPositions}
   * @param {!AccountPositions} other Other struct
   * @returns {!AccountPositions} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.Positions != null) {
      this.Positions = new Map()
      Object.keys(other.Positions).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = new UUID(key)
          if (other.Positions[key] != null) {
            let tempValue
            tempValue = Position.fromObject(other.Positions[key])
            this.Positions.set(tempKey, tempValue)
          } else {
            this.Positions.set(tempKey, null)
          }
        }
      })
    } else {
      this.Positions = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountPositions}
   * @returns {!AccountPositions} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountPositionsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountPositionsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountPositions}
   * @param {!AccountPositions} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountPositions)) {
      throw new TypeError('Instance of AccountPositions is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountPositions}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      Positions: ((this.Positions != null) ? fbe.MapToObject(new Map(Array.from(this.Positions, item => [((item[0] != null) ? item[0].toString() : null), ((item[1] != null) ? item[1] : null)]))) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountPositions.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountPositions} other Object value
   * @returns {!AccountPositions} Created struct
   */
  static fromObject (other) {
    return new AccountPositions().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountPositions}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountPositions.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountPositions}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 56
  }
}

export { AccountPositions };

/**
 * Fast Binary Encoding AccountPositions field model
 */
class FieldModelAccountPositions extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Positions = new fbe.FieldModelMap(new fbe.FieldModelUUID(buffer, this.Login.fbeOffset + this.Login.fbeSize), new FieldModelPosition(buffer, this.Login.fbeOffset + this.Login.fbeSize), buffer, this.Login.fbeOffset + this.Login.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.Positions.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.Positions.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountPositions.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 56
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountPositions}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountPositions}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Positions.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Positions.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Positions.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountPositions}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountPositions}
   * @param {!AccountPositions} fbeValue Default value, defaults is new AccountPositions()
   * @returns {!AccountPositions} AccountPositions value
   */
  get (fbeValue = new AccountPositions()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountPositions}
   * @param {!AccountPositions} fbeValue AccountPositions value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Positions.fbeSize) <= fbeStructSize) {
      this.Positions.get(fbeValue.Positions)
    } else {
      fbeValue.Positions.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Positions.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountPositions}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountPositions}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountPositions}
   * @param {!AccountPositions} fbeValue AccountPositions value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountPositions}
   * @param {AccountPositions} fbeValue AccountPositions value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.Positions.set(fbeValue.Positions)
  }
}

export { FieldModelAccountPositions };

/**
 * Fast Binary Encoding AccountPositions model
 */
class AccountPositionsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountPositions(this.buffer, 4)
  }

  /**
   * Get the AccountPositions model
   * @this {!AccountPositionsModel}
   * @returns {!FieldModelAccountPositions} model AccountPositions model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountPositionsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountPositionsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountPositionsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountPositionsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountPositions.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountPositionsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountPositionsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountPositionsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountPositionsModel}
   * @param {!AccountPositions} value AccountPositions value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountPositionsModel}
   * @param {!AccountPositions} value AccountPositions value, defaults is new AccountPositions()
   * @return {!object} Deserialized AccountPositions value and its size
   */
  deserialize (value = new AccountPositions()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountPositions(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountPositions(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountPositionsModel}
   * @param {!number} prev Previous AccountPositions model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountPositionsModel };
